// ── EVIDENCE P1.2c — the in-product Microsoft consent step (Gate 1, FIRST) ─────────────────────────
//
// ⚠ CONSENT IS VERIFIED, NOT ASSUMED. The admin approves on Microsoft's page, lands on an inert static
//   page (https://app.verigrc.com/consent-complete.html) that VeriGRC never reads, returns here, and
//   selects "Check admin consent". The server mints an app-only token for the directory NOW and reads
//   the granted permission from it (D-EV-CONSENT-VERIFY). Nothing on Microsoft's redirect is consulted.
//
// ⚠ THE ORDER OF THE GUIDANCE IS RATIFIED (§3.6/§3.7): the in-product approval page is offered FIRST
//   in every outcome; the Entra portal's "Grant admin consent" button is a FALLBACK, and only once the
//   VeriGRC Compliance enterprise application exists (opening the approval page once creates it).
//
// ⓘ WHY (a) AND (b) ARE OFFERED RATHER THAN DETECTED. "Declined" and "not an administrator" are only
//   ever told to the browser, on the redirect to a page that — by design (D21) — runs no code and
//   keeps no state. What VeriGRC can verify is the result: consent present or absent. So when it is
//   absent, the admin is shown what to do for each way it can have gone wrong.
//
// ⓘ THE LEAST-PRIVILEGE STATEMENT IS THE SERVER'S GATE-1 BODY, shown directly above this step; it is
//   not repeated here (D-EV-CONSENT-UX-COPY-REV-API).
import { useState } from 'react';
import { CheckCircle2, ExternalLink, Loader2, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConsentCheck } from '@/api/hooks/useEvidenceOnboarding';
import type { ConsentCheckResponse } from '@/api/types/evidence-onboarding.types';

/**
 * The consent step's copy — asserted verbatim by the page tests.
 *
 * ⚠ `declined` AND `notAdmin` SUPERSEDE THE §3.7 RATIFIED WORDING (D-EV-CONSENT-UX-COPY-REV-API, MI):
 *   no named Entra roles — "an authorized Microsoft Entra administrator who can grant tenant-wide admin
 *   consent"; the KB lists the verified roles. Both are still GUIDANCE inside the not-consented state,
 *   never detected outcomes (see above).
 */
export const CONSENT_COPY = {
  helper: 'Enter the Directory (tenant) ID from Microsoft Entra admin center → Overview. This is a UUID, '
    + 'not your Microsoft 365 domain or VeriGRC tenant name.',
  initial: 'Enter your Microsoft Entra tenant ID.',
  notConsented: 'Not yet consented. The approval link must be opened by an authorized Microsoft Entra '
    + 'administrator who can grant tenant-wide admin consent.',
  declined: 'Admin consent was not granted. No access was given.',
  notAdmin: 'This step must be completed by an authorized Microsoft Entra administrator who can grant '
    + 'tenant-wide admin consent.',
  consented: 'Admin consent confirmed.',
} as const;

const UNVERIFIABLE: Record<string, string> = {
  transient: 'Microsoft is busy right now. Wait a minute, then check again.',
  platform_not_configured: 'VeriGRC cannot check consent right now — this is a VeriGRC-side problem. Nothing was changed.',
  platform_credential: 'VeriGRC cannot check consent right now — this is a VeriGRC-side problem. Nothing was changed.',
  claims_mismatch: 'VeriGRC could not confirm Microsoft\'s answer. Check again; if this keeps happening, contact support.',
  // ⚠ THE WHOLE CUSTOMER-FACING MESSAGE. Microsoft's code stays in the audit row, never on the page.
  refused: 'We couldn\'t verify admin consent for that tenant ID. Confirm you entered the Directory (tenant) '
    + 'ID (a UUID) from Microsoft Entra admin center → Overview, and that an administrator has granted '
    + 'admin consent. If both are right, ask your administrator whether the VeriGRC Compliance application '
    + 'has been disabled in Microsoft Entra.',
};

export function ConsentStep({ directoryId, onResult }: {
  directoryId: string;
  /** Told whether consent is verified, so the page can enable "Continue" only then. */
  onResult: (verified: boolean) => void;
}) {
  const check = useConsentCheck();
  const [answer, setAnswer] = useState<ConsentCheckResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const run = () => {
    setFailure(null);
    check.mutate({ directory_id: directoryId.trim() }, {
      onSuccess: (r) => { setAnswer(r); onResult(r.consent.state === 'consented'); },
      onError: (e: unknown) => {
        setAnswer(null);
        onResult(false);
        const msg = (e as { response?: { data?: { errors?: Array<{ message?: string }> } } })
          ?.response?.data?.errors?.[0]?.message;
        setFailure(msg ?? 'VeriGRC could not check consent. Nothing was changed.');
      },
    });
  };

  const consent = answer?.consent;
  return (
    <div className="space-y-3 rounded border border-border p-3" data-testid="consent-step">
      <p className="text-xs text-text-secondary" data-testid="consent-tenant-help">{CONSENT_COPY.helper}</p>

      <p className="text-sm">
        <strong>Step 1 — Microsoft Entra admin consent.</strong> An authorized Microsoft Entra administrator
        who can grant tenant-wide admin consent completes this step. Select <em>Check admin consent</em>:
        VeriGRC asks Microsoft directly and, if approval is still needed, gives you the approval link.
      </p>

      {!directoryId.trim() && (
        <p className="text-sm text-text-secondary" data-testid="consent-initial">{CONSENT_COPY.initial}</p>
      )}

      <Button type="button" variant="outline" onClick={run}
              disabled={check.isPending || !directoryId.trim()} data-testid="consent-check">
        {check.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
        Check admin consent
      </Button>

      {failure && <p role="alert" className="text-sm text-danger" data-testid="consent-error">{failure}</p>}

      {consent?.state === 'consented' && (
        <p className="flex items-start gap-2 text-sm" data-testid="consent-verified">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
          <span>
            {CONSENT_COPY.consented} Microsoft Entra tenant ID:{' '}
            <span className="font-mono" data-testid="consent-verified-tenant">{answer?.directory_id}</span>
          </span>
        </p>
      )}

      {consent?.state === 'not_consented' && (
        <div className="space-y-2 text-sm" data-testid="consent-needed">
          <p className="flex items-start gap-2 font-medium" data-testid="consent-not-yet">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" /> {CONSENT_COPY.notConsented}
          </p>
          {/* ⚠ FIRST, IN EVERY MODE: the in-product approval page (§3.7). */}
          {answer?.consent_url && (
            <a href={answer.consent_url} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1 text-primary underline" data-testid="consent-link">
              Open the Microsoft approval page <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <p className="text-text-secondary">
            After the administrator accepts, Microsoft shows a “consent complete” page — return here and
            select <em>Check admin consent</em> again.
          </p>
          <div className="space-y-2 rounded bg-bg-surface-2 p-2" data-testid="consent-guidance">
            <p className="font-medium">If it did not work</p>
            <p data-testid="consent-mode-declined">
              <strong>You cancelled on Microsoft&apos;s page.</strong> {CONSENT_COPY.declined} Open the approval
              page again to retry.
            </p>
            <p data-testid="consent-mode-not-admin">
              <strong>Microsoft said you need an administrator.</strong> {CONSENT_COPY.notAdmin} Send them this
              approval link:
            </p>
            {answer?.consent_url && (
              <input readOnly value={answer.consent_url} aria-label="Approval link to forward"
                     className="w-full rounded border border-border bg-bg-surface px-2 py-1 text-xs"
                     data-testid="consent-link-forward" onFocus={(e) => e.currentTarget.select()} />
            )}
            {/* ⚠ SECOND, AND ONLY AFTER THE ENTERPRISE APP EXISTS (§3.6). */}
            <p data-testid="consent-manual-fallback">
              <strong>Fallback — only if the approval page cannot be used.</strong> Once{' '}
              <em>VeriGRC Compliance</em> appears in Microsoft Entra under <em>Enterprise applications</em>
              (opening the approval page once creates it), an authorized Microsoft Entra administrator who
              can grant tenant-wide admin consent can open it and select
              <em> Permissions → Grant admin consent</em>. It grants exactly the same single permission, and
              it gives VeriGRC no access to any library on its own.
            </p>
          </div>
        </div>
      )}

      {consent?.state === 'unverifiable' && (
        <p role="alert" className="text-sm text-danger" data-testid="consent-unverifiable">
          {UNVERIFIABLE[consent.reason] ?? UNVERIFIABLE.claims_mismatch}
        </p>
      )}
    </div>
  );
}
